import extend from '@ep/bootstrap/extend';
import navigo from '@ep/plugins/navigo';
import Common from '@ep/app/common';
import '@ep/styles/style.scss';
import '../styles/index.scss';
import '../styles/pages/unlogged/index.scss';
import '../styles/pages/logged/index.scss';
import ajax from '@ep/tools/ajax';
import { setupTrackingConsent } from '@ep/tools/tracking';

/**
 * Inicializa extensões globais de protótipos antes do roteamento.
 */
extend();

/* ===== Roteador ===== */
/**
 * Camada de roteamento da aplicação baseada em Navigo.
 */
class Router extends Common {
  async #resolveCurrentLocation() {
    const config = await ajax.getParam(undefined, { loader: false });
    const languageConfig = config?.language && typeof config.language === 'object' ? config.language : {};
    const defaultLang = typeof languageConfig.default === 'string' && languageConfig.default.trim()
      ? languageConfig.default.trim()
      : 'pt';
    const supportedLangs = Array.isArray(languageConfig.supported) && languageConfig.supported.length > 0
      ? languageConfig.supported
      : [defaultLang];
    const segments = window.location.pathname
      .split('/')
      .filter(Boolean)
      .map((segment) => {
        try {
          return decodeURIComponent(segment).trim();
        } catch (_) {
          return String(segment || '').trim();
        }
      })
      .filter(Boolean);

    if (segments.length === 0) {
      return { lang: null, page: null };
    }

    if (segments.length === 1) {
      const [first] = segments;

      if (first === '404') {
        return { lang: null, page: '404' };
      }

      if (supportedLangs.includes(first)) {
        return { lang: first, page: null };
      }

      return { lang: null, page: first };
    }

    if (segments.length === 2) {
      const [lang, page] = segments;

      if (!supportedLangs.includes(lang)) {
        return { lang: null, page: '404' };
      }

      return { lang, page };
    }

    return { lang: null, page: '404' };
  }

  async #renderCurrentLocation() {
    const route = await this.#resolveCurrentLocation();
    await this.initializePage(route.lang, route.page);
    setupTrackingConsent(
      window?.config?.branding?.tracking || window?.dic?.branding?.tracking || {},
      {
        labels: window?.dic?.privacy,
        privacyUrl: window?.dic?.branding?.privacyUrl || window?.config?.branding?.privacyUrl || '',
      },
    );
  }

  /**
   * Registra rotas e resolve navegação inicial.
   * @public
   * @returns {Promise<void>}
   */
  async render() {
    navigo
      .on({
        '/': async () => {
          await this.#renderCurrentLocation();
        },
        '/404': async () => {
          await this.#renderCurrentLocation();
        },
        '/:page': async () => {
          await this.#renderCurrentLocation();
        },
        '/:lang/:page': async () => {
          await this.#renderCurrentLocation();
        },
      })
      .notFound(() => {
        this.initializePage(null, '404');
      })
      .resolve();
  }
}

const router = new Router();

/* ===== API publica ===== */
/**
 * Função pública de bootstrap do roteador.
 * @public
 * @returns {Promise<void>}
 */
export const Render = () => router.render();

/**
 * Recarrega o fluxo da rota atual sem depender de mudança de URL.
 * @public
 * @returns {Promise<void>}
 */
export const RefreshCurrentRoute = async () => {
  await navigo.resolve();
};
